import { apiService } from './apiService';
import { API_CONFIG } from '../config/apiConfig';
import { Order, PaymentMethod, OrderItem, VendaFormaPagamento } from '../types';
import { getDateInBrasiliaTimezone } from '@/lib/utils';

interface ErpVendaRequest {
  ID: number;
  Data: string;
  FormaPagamentoID: number;
  VendaID: number;
  IdentificadorVendaPDV: string;
  EmpresaID: number;
  ClienteID: number;
  VendedorID: number;
  TipoOperacaoID: number;
  ValorDesconto: number;
  TotalPedido: number;
  Observacao?: string;
  CpfCnpjCliente?: string;
  Troco: number;
  NumeroComanda?: string;
  FaturaAutomatico?: boolean;
  VendaItem: ErpVendaItem[];
  VendaFormaPagamento: ErpVendaFormaPagamento[];
}

interface ErpVendaItem {
  ProdutoID: number;
  ProdutoNome: string;
  Quantidade: number;
  Valor: number;
  SubTotal: number;
  ValorDesconto: number;
  Total: number;
  UnidadeMedidaID: number;
}

interface ErpVendaFormaPagamento {
  FormaPagamentoID: number;
  FormaPagamentoNome: string;
  Valor: number;
  DataVencimento: string;
}

export const generateGuid = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export function isValidGuid(str: string): boolean {
  const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return guidRegex.test(str);
}

const mapOrderItemsToErpFormat = (items: OrderItem[]): ErpVendaItem[] => {
  return items.map(item => ({
    ProdutoID: item.product.erpProductId || parseInt(item.product.id, 10),
    ProdutoNome: item.product.name,
    Quantidade: item.quantity,
    Valor: item.product.price,
    SubTotal: item.quantity * item.product.price,
    ValorDesconto: 0,
    Total: item.quantity * item.product.price,
    UnidadeMedidaID: 1
  }));
};

export const getOrCreateTableExternalId = (tableId: string): string => {
  const tableExternalIdKey = `table_external_id_${tableId}`;
  let externalId = localStorage.getItem(tableExternalIdKey);
  
  if (!externalId || !isValidGuid(externalId)) {
    externalId = generateGuid();
    localStorage.setItem(tableExternalIdKey, externalId);
  }
  
  return externalId;
};

export const ensureOrderExternalId = (order: Order): Order => {
  if (!order.externalId || !isValidGuid(order.externalId)) {
    order.externalId = getOrCreateTableExternalId(order.tableId);
  }
  return order;
};

export const clearTableData = (tableId: string): void => {
  try {
    const tableExternalIdKey = `table_external_id_${tableId}`;
    localStorage.removeItem(tableExternalIdKey);
    
    console.log(`Table data cleared for table ID: ${tableId}`);
  } catch (error) {
    console.error('Error clearing table data:', error);
  }
};

export const createErpVendaRequest = (
  order: Order, 
  paymentMethods?: PaymentMethod[],
  paymentValues?: number[],
  vendedorId: number = 1,
  clienteID: number = 0,
  faturarAutomatico: boolean = false,
  tableGuid?: string
): ErpVendaRequest => {
  if (!order.externalId || !isValidGuid(order.externalId)) {
    order.externalId = getOrCreateTableExternalId(order.tableId);
  }

  let vendaFormaPagamento: ErpVendaFormaPagamento[] = [];
  
  if (paymentMethods && paymentMethods.length > 0 && paymentValues && paymentValues.length === paymentMethods.length) {
    vendaFormaPagamento = paymentMethods.map((method, index) => ({
      FormaPagamentoID: parseInt(method.id, 10) || 1,
      FormaPagamentoNome: method.name,
      Valor: paymentValues[index],
      DataVencimento: getDateInBrasiliaTimezone(order.createdAt).toISOString()
    }));
  } else {
    vendaFormaPagamento = [{
      FormaPagamentoID: 1,
      FormaPagamentoNome: "Dinheiro",
      Valor: order.totalAmount,
      DataVencimento: getDateInBrasiliaTimezone(order.createdAt).toISOString()
    }];
  }
  
  const brasiliaDate = getDateInBrasiliaTimezone(order.createdAt);
  
  const finalClienteID = clienteID || (order.customerId ? parseInt(order.customerId, 10) : 0);
  
  console.log(`ClienteID para sincronização: ${finalClienteID}`);
  console.log(`ExternalID (GUID) para sincronização: ${order.externalId}`);
  console.log(`Documento para sincronização: ${order.document || 'Não informado'}`);
  console.log(`Faturar automático: ${faturarAutomatico}`);
  
  const principalPaymentMethodId = paymentMethods && paymentMethods.length > 0 
    ? parseInt(paymentMethods[0].id, 10) || 1 
    : 1;
  
  const vendaRequest: ErpVendaRequest = {
    ID: order.erpId || 0,
    Data: brasiliaDate.toISOString(),
    FormaPagamentoID: principalPaymentMethodId,
    VendaID: order.erpId || 0,
    IdentificadorVendaPDV: order.externalId,
    EmpresaID: parseInt(localStorage.getItem('empresa_id') || '1', 10),
    ClienteID: finalClienteID,
    VendedorID: vendedorId,
    TipoOperacaoID: 0,
    ValorDesconto: 0,
    TotalPedido: order.totalAmount,
    Observacao: order.observations || "",
    CpfCnpjCliente: order.document || "",
    Troco: order.changeAmount || 0,
    NumeroComanda: order.tableNumber?.toString() || "",
    FaturaAutomatico: faturarAutomatico,
    VendaItem: mapOrderItemsToErpFormat(order.items),
    VendaFormaPagamento: vendaFormaPagamento
  };
  
  return vendaRequest;
};

export const syncOrderWithErp = async (
  order: Order, 
  paymentMethods?: PaymentMethod[],
  paymentValues?: number[],
  vendedorId: number = 1,
  clienteID: number = 0,
  faturarAutomatico: boolean = false,
  tableGuid?: string
): Promise<number> => {
  try {
    API_CONFIG.initialize();
    
    if (!API_CONFIG.baseURL) {
      console.error('API base URL is not configured');
      throw new Error('URL da API não configurada');
    }
    
    const endpoint = API_CONFIG.endpoints.orders.syncOrder;
    const vendaRequest = createErpVendaRequest(order, paymentMethods, paymentValues, vendedorId, clienteID, faturarAutomatico, tableGuid);
    
    console.log('Sync order with ERP:', {
      baseURL: API_CONFIG.baseURL,
      endpoint,
      hasApiKey: !!API_CONFIG.apiKey,
      order: {
        id: order.id,
        status: order.status,
        totalAmount: order.totalAmount,
        itemsCount: order.items.length,
        externalId: order.externalId,
        document: order.document,
        observations: order.observations
      },
      paymentMethods: paymentMethods?.map(pm => pm.name) || 'Não especificado',
      vendedorId,
      clienteID,
      faturarAutomatico,
      tableGuid
    });
    
    console.log('Sync request data:', vendaRequest);
    
    const response = await apiService.post(endpoint, vendaRequest);
    
    console.log('Sync response:', response);

    if (vendaRequest.VendaFormaPagamento && vendaRequest.VendaFormaPagamento.length > 0) {
      order.VendaFormaPagamento = vendaRequest.VendaFormaPagamento;
      
      const { updateOrder } = await import('./storageService');
      await updateOrder(order);
      
      console.log('Updated order with payment methods:', order.VendaFormaPagamento);
    }
    
    if (typeof response === 'number') {
      return response;
    } else if (response && typeof response.ID === 'number') {
      return response.ID;
    } else {
      console.warn('Unexpected response format:', response);
      return 0;
    }
  } catch (error) {
    console.error('Error syncing order with ERP:', error);
    throw error;
  }
};

export interface DanfeResponse {
  mensagem: string;
  success: boolean;
  danfe: string | null;
  xml: string | null;
}

export const requestDanfePdf = async (orderId: number): Promise<DanfeResponse> => {
  try {
    API_CONFIG.initialize();
    
    if (!API_CONFIG.baseURL) {
      console.error('API base URL is not configured');
      throw new Error('URL da API não configurada');
    }
    
    const endpoint = '/api/vendas/ImprimirDanfePorID';
    
    console.log(`Requesting DANFE for order ID: ${orderId}`);
    
    const response = await apiService.post<DanfeResponse>(endpoint, orderId);
    
    console.log('DANFE response received:', {
      success: response.success,
      hasMessage: !!response.mensagem,
      hasPdf: !!response.danfe,
      hasXml: !!response.xml
    });
    
    return {
      mensagem: response.mensagem || '',
      success: response.success || false,
      danfe: response.danfe || null,
      xml: response.xml || null
    };
  } catch (error) {
    console.error('Error requesting DANFE PDF:', error);
    throw error;
  }
};

export const testApiConnection = async (): Promise<boolean> => {
  try {
    API_CONFIG.initialize();
    
    if (!API_CONFIG.baseURL) {
      console.error('API base URL is not configured');
      return false;
    }
    
    const endpoint = '/api/ping';
    const response = await apiService.get(endpoint);
    
    return !!response;
  } catch (error) {
    console.error('Error testing API connection:', error);
    return false;
  }
};

export const isOrderModifiedSinceLastSync = (order: Order): boolean => {
  if (!order.lastSyncDate) return true;
  
  const lastSyncTime = new Date(order.lastSyncDate).getTime();
  const updateTime = new Date(order.updatedAt).getTime();
  
  return updateTime > lastSyncTime;
};

export const calculateSubtotal = (order: Order): number => {
  if (!order.items || order.items.length === 0) return 0;
  
  return order.items.reduce((sum, item) => {
    if (item.status === 'cancelled') return sum;
    return sum + (item.product.price * item.quantity);
  }, 0);
};

export const calculateDiscountValue = (order: Order): number => {
  const subtotal = calculateSubtotal(order);
  
  if (!order.discount) return 0;
  
  if (order.discountType === 'percentage') {
    return (subtotal * order.discount) / 100;
  } else {
    return Math.min(order.discount, subtotal);
  }
};

export const calculateOrderTotal = (order: Order): number => {
  const subtotal = calculateSubtotal(order);
  const discountValue = calculateDiscountValue(order);
  
  return subtotal - discountValue;
};

export const calculateTableTotal = (orders: Order[]): number => {
  if (!orders || orders.length === 0) return 0;
  
  return orders.reduce((sum, order) => {
    if (order.status === 'cancelled') return sum;
    return sum + calculateOrderTotal(order);
  }, 0);
};
